import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import classNames from 'classnames';
import get from 'lodash/get';
import isEmpty from 'lodash/isEmpty';
import includes from 'lodash/includes';
import { FormattedMessage } from 'react-intl';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faWindows as fabFaWindows,
  faApple as fabFaApple,
  faLinux as fabFaLinux,
} from '@fortawesome/free-brands-svg-icons';

import 'Components/Carousel/FeaturedCarousel.scss';

import Drm from 'Components/Utility/Drm';
import FlashSaleProgressBar from 'Components/Carousel/FeaturedCarouselPanels/PanelComponents/FlashSaleProgressBar';
import LinkTag from 'Components/Carousel/LinkTag';
import PanelSelector from 'Components/Carousel/FeaturedCarouselPanels/PanelSelector';
import PriceContainer from 'Components/Utility/Price/PriceContainer';
import ProductCover from 'Components/Utility/ProductCover';
import StarDealProgressBar from 'Components/Carousel/FeaturedCarouselPanels/PanelComponents/StarDealProgressBar';
import store from 'Redux/store';
import useViewport from 'Hooks/useViewport';


function FeaturedCarouselSlide({
  slide,
  slideNumber,
  starDeal,
  fantasyVerse,
}) {
  const { isBelowLg } = useViewport();

  let slideProductType = 'game';
  if (slide.type === 'product' && !isEmpty(slide.hit)) {
    slideProductType = slide.hit.display_type || 'game';
  }

  const [slideImg, setSlideImg] = useState({ cover: slide.img, type: slideProductType, hit: {} });

  useEffect(() => {
    setSlideImg({ cover: slide.img, type: slideProductType });
  }, [slide, setSlideImg, slideProductType]);

  const slideUrl = get(slide, 'url', null);
  const slideHit = get(slide, 'hit', {});
  const slideExternalLink = get(slide, 'external_link', false);
  const slideType = get(slide, 'type', '');
  const slideImgAltText = get(slide, 'img_alt_text', '');
  let slideName = get(slide, 'name', '');

  const slideHitName = get(slideHit, 'name', '');
  const slideHitCoverAltText = get(slideHit, 'cover_alt_text', '');

  if ((slideType === 'product' || slideType === 'bundle' || slideType === 'starDeal') && slideHit && slideHitName) {
    slideName = slideHitName;
  }

  const switchCoverImg = (image) => {
    if (isEmpty(image)) {
      setSlideImg({ cover: slide.img, type: slideProductType });
    } else {
      setSlideImg({ cover: image.cover, type: image.type, hit: image.hit });
    }
  };

  let coverAltText = `Product cover for ${slideName}`;
  if (slideHitCoverAltText) coverAltText = slideHitCoverAltText;
  if (slideImgAltText) coverAltText = slideImgAltText;

  const logClick = () => {
    store.dispatch({
      type: 'frontend/carousel/CLICKED',
      payload: { advertId: slide._id, url: slide.url, index: slideNumber, component: 'FeaturedCarouselSlide' },
    });

    const section = fantasyVerse ? 'FantasyVerseCarousel' : 'MainCarousel';
    store.dispatch({
      type: 'frontend/click/CLICK_INTERACTION',
      payload: { section, value: slideUrl, context: `Slide${slideNumber}` },
    });
  };

  let validDrm = '';
  let filteredDrm = [];
  if (slideImg.hit && slideImg.hit.drm) filteredDrm = slideImg.hit.drm.filter(d => d !== 'voucher');
  if (filteredDrm.length === 1) validDrm = filteredDrm[0];

  const validPlatforms = get(slideImg, 'hit.operating_systems', []);

  const starDealSlug = get(starDeal, 'slug', '');

  return (
    <article className={classNames('FeaturedCarousel__slideContainer')}>
      <div className={classNames('FeaturedCarousel__image', { 'FeaturedCarousel__image--contain': !slideImg.type })}>
        <LinkTag
          url={slideUrl}
          externalLink={slideExternalLink}
          onClick={() => logClick()}
        >
          <ProductCover
            cover={slideImg.cover}
            alt={coverAltText}
            displayType={slideImg.type}
            sizesOverride={'(min-width: 1520px) 883px, (min-width: 1200px) 740px, (min-width: 992px) 620px, 95vw'}
          />
        </LinkTag>

        {isBelowLg &&
          <div className="FeaturedCarousel__image__mobileOverlay">
            {(slide.type === 'starDeal' || (slide.type === 'product' && starDealSlug === slide.hit.slug)) &&
              <StarDealProgressBar
                keysAvailablePercentage={slide.hit.keysAvailablePercentage}
                starDealEndDate={slide.hit.available_valid_until * 1000}
              />
            }
            {slide.type === 'flashDeal' &&
              <FlashSaleProgressBar
                startingDate={slide.hit.available_valid_from}
                endingDate={slide.hit.available_valid_until}
              />
            }
          </div>
        }

        {!isEmpty(slideImg.hit) &&
          <div className={classNames('FeaturedCarousel__image__price', { 'FeaturedCarousel__image__price--value': slide.type === 'bundle' })}>
            {slide.type === 'bundle' &&
              <div className="FeaturedCarousel__image__price__value">
                <FormattedMessage id="featured-carousel-slide.price.value" defaultMessage="Value:" />
              </div>
            }
            <PriceContainer
              discountPercent={slideImg.hit.discount_percent}
              price={slideImg.hit.price}
              tiered={slideImg.hit.tiered}
              hideDiscount={slideImg.hit.hide_discount}
              slug={slideImg.hit.slug}
              fullPrice={slideImg.hit.fullPrice}
              type={slideImg.hit.type}
            />
          </div>
        }

        {!isEmpty(slideImg.hit) && !isEmpty(validDrm[0]) && !isEmpty(validPlatforms) &&
          <div className="FeaturedCarousel__image__icons">
            {!isEmpty(validDrm[0]) &&
              <Drm drmType={validDrm} iconSize="small" className={isEmpty(validPlatforms) ? 'mr-0' : ''} />
            }
            {includes(validPlatforms, 'windows') &&
              <span className="FeaturedCarousel__image__icons__platform">
                <FontAwesomeIcon icon={fabFaWindows} />
              </span>
            }
            {includes(validPlatforms, 'mac') &&
              <span className="FeaturedCarousel__image__icons__platform">
                <FontAwesomeIcon icon={fabFaApple} />
              </span>
            }
            {includes(validPlatforms, 'linux') &&
              <span className="FeaturedCarousel__image__icons__platform">
                <FontAwesomeIcon icon={fabFaLinux} />
              </span>
            }
          </div>
        }

      </div>
      <aside className="FeaturedCarousel__carouselPanel">
        <div className="FeaturedCarousel__carouselPanelLink faux-block-link">
          <PanelSelector
            slide={slide}
            slideClickFunction={logClick}
            slideExternalLink={slideExternalLink}
            slideUrl={slideUrl}
            switchCoverImg={switchCoverImg}
            fantasyVerse={fantasyVerse}
          />
        </div>
      </aside>
    </article>
  );
}

FeaturedCarouselSlide.propTypes = {
  slide: PropTypes.shape({}),
  slideNumber: PropTypes.number,
  starDeal: PropTypes.shape({}),
  fantasyVerse: PropTypes.bool,
};

FeaturedCarouselSlide.defaultProps = {
  slide: {},
  slideNumber: 0,
  starDeal: {},
  fantasyVerse: false,
};

const mapStateToProps = ({ starDeal }) => ({ starDeal });

export default connect(mapStateToProps)(FeaturedCarouselSlide);
